import { getApp, getApps, initializeApp, type FirebaseApp } from "firebase/app";
import { getAuth, type Auth } from "firebase/auth";
import { getFirestore, type Firestore } from "firebase/firestore";
import { getStorage, type FirebaseStorage } from "firebase/storage";
import { getFunctions, type Functions } from "firebase/functions";

const config = {
  apiKey:
    // Vite exposes env vars on `import.meta.env` when prefixed with `VITE_`.
    import.meta.env.VITE_FIREBASE_API_KEY ?? import.meta.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain:
    import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? import.meta.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId:
    import.meta.env.VITE_FIREBASE_PROJECT_ID ?? import.meta.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket:
    import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ?? import.meta.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId:
    import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? import.meta.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID ?? import.meta.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  measurementId:
    import.meta.env.VITE_FIREBASE_MEASUREMENT_ID ?? import.meta.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
};

// Only require the core client values. Some projects omit measurementId or messagingSenderId.
const requiredKeys = ['apiKey', 'authDomain', 'projectId', 'appId'];
const hasValidConfig = requiredKeys.every((k) => Boolean((config as any)[k]));

let firebaseApp: FirebaseApp | null = null;
let firestore: Firestore | null = null;
let firebaseAuth: Auth | null = null;
let storage: FirebaseStorage | null = null;
let functions: Functions | null = null;

if (hasValidConfig) {
  firebaseApp = getApps().length ? getApp() : initializeApp(config);
  firestore = getFirestore(firebaseApp);
  firebaseAuth = getAuth(firebaseApp);
  storage = getStorage(firebaseApp);
  functions = getFunctions(firebaseApp);
} else {
  if (import.meta.env.DEV) {
    const missing = Object.entries(config).filter(([, v]) => !v).map(([k]) => k);
    console.warn("Firebase client config missing. Falling back to mock data.");
    console.warn("Resolved firebase config keys (masked):", {
      apiKey: config.apiKey ? '***' : null,
      authDomain: config.authDomain || null,
      projectId: config.projectId || null,
      storageBucket: config.storageBucket || null,
      messagingSenderId: config.messagingSenderId || null,
      appId: config.appId ? '***' : null,
      measurementId: config.measurementId || null,
      missingKeys: missing,
    });
  }
}

export function getFirebaseServices() {
  if (!firebaseApp || !firestore || !firebaseAuth || !storage) {
    return null;
  }

  return {
    app: firebaseApp,
    db: firestore,
    auth: firebaseAuth,
    storage,
    functions,
  };
}
